/**
 * #5270 step 6b: delete the IndexedDB generation a device abandoned when it ROLLED BACK.
 *
 * **Read this before touching anything here, because the danger is not obvious.** A
 * rollback (#4981 phase 4) does not reuse the device's old IndexedDB databases. It copies
 * the data into a FRESH #4401 generation — `userdb-fr_abc` becomes `userdb-fr_abc__g1` —
 * and leaves the old set on disk. The app's own database provider only ever opens the
 * CURRENT generation, so it looks straight past them, and the native plugin cannot list
 * its own files. The rolled-back marker's `supersededIndexedDbNames` is therefore the ONLY
 * thing left that can still name them. Without this module a rolled-back device keeps a
 * complete second copy of the user's data for ever.
 *
 * **Why this is a separate file from `legacy-idb-cleanup.ts`, and must stay one.** That
 * cleanup deletes EVERY IndexedDB database named `_pouch_*`, with no generation filter.
 * It is safe where it runs because its gate requires the device to be ON SQLITE, where no
 * such database is live. This one runs on a device that is ON INDEXEDDB, whose LIVE
 * databases sit under that same prefix, right beside the ones being deleted. Routing a
 * rolled-back device into that sweep would delete the data it is using. A separate module
 * whose only input is a name list is what makes that boundary visible to the next reader.
 * The repo already uses this "same helper, opposite policy, separate modules" split — see
 * the note at the top of `legacy-idb-cleanup.ts`.
 *
 * Angular-free, like its siblings, so it can be driven directly from a unit test.
 *
 * The deleting itself and the bounded wait are shared with the sign-out wipe and the
 * forward cleanup, in `idb-database-deletion.ts`. What is here is this cleanup's own
 * policy: which databases it may touch, which it must REFUSE to touch, when it may retry,
 * and what it clears when it is done.
 */

import { errorReportFields } from '../services/error-patterns';
import {
  currentLocalStoreName,
  GenerationStorage,
  stripLocalStoreGeneration,
} from '../services/local-store-generation';
import {
  readRolledBackMarker,
  serialiseRolledBackMarker,
} from '../services/sqlite-migration-decisions';
import { SQLITE_MIGRATION_ROLLED_BACK_KEY } from '../services/local-database-engine-decisions';
import {
  CleanupProblemReporter,
  CLEANUP_DELETION_TIMEOUT_MS,
  listPouchIndexedDbNamesAsync,
} from './legacy-idb-cleanup';
import {
  deleteIdbDatabasesAsync,
  IdbDeletionOutcome,
  IdbDeletionResult,
  IdbForDeletion,
} from './idb-database-deletion';

/** Databases named with this prefix are PouchDB's. Same prefix the forward cleanup uses. */
const POUCH_DB_NAME_PREFIX = '_pouch_';

/**
 * What separates a PouchDB view index from its parent database in the name.
 *
 * PouchDB keeps each map/reduce view's index in its own IndexedDB database named
 * `_pouch_<parent>-mrview-<hash>`. Those are derived data: delete one and PouchDB rebuilds
 * it from the documents on the next query.
 */
const VIEW_INDEX_SEPARATOR = '-mrview-';

/**
 * localStorage key counting how many boots have tried this cleanup.
 *
 * **Its own budget, deliberately NOT shared with the forward cleanup's.** Sharing would
 * let a spent forward budget block this path or the other way round, and the forward
 * budget is now cleared on a re-migration by `SqliteLifecycle.reArmLegacyIdbCleanup`,
 * which means nothing here.
 *
 * Not on either localStorage survivor path, for the same reason the forward cleanup's
 * attempts key is not: a sign-out should hand the next account a fresh budget rather than
 * a spent one.
 */
export const SUPERSEDED_CLEANUP_ATTEMPTS_KEY = 'cs_superseded_idb_cleanup_attempts';

/** How many boots may try this cleanup before it gives up and stops retrying. */
export const MAX_SUPERSEDED_CLEANUP_ATTEMPTS = 3;

/** Problem name for a run that threw before it could finish issuing its deletions. */
export const SUPERSEDED_CLEANUP_FAILED_PROBLEM = 'AndroidSqliteRollbackIdbCleanupFailed';

/** Problem name for one or more deletions that did not confirm. */
export const SUPERSEDED_DELETION_FAILED_PROBLEM = 'AndroidSqliteRollbackIdbDeletionFailed';

/** Problem name for the last attempt, after which this cleanup stops retrying. */
export const SUPERSEDED_CLEANUP_GAVE_UP_PROBLEM = 'AndroidSqliteRollbackIdbCleanupGaveUp';

/**
 * Problem name for a recorded name that is ALSO the name this device is using right now.
 *
 * This is not a theoretical shape — {@link refuseLiveNames} says how a device reaches it.
 * It means something durable disagrees with something that was wiped, so it is worth a
 * report of its own rather than being folded into a generic failure.
 */
export const SUPERSEDED_NAME_IS_LIVE_PROBLEM = 'AndroidSqliteRollbackSupersededNameIsLive';

/** Every console line this cleanup writes carries it, so a reader knows which pass wrote them. */
const LOG_PREFIX = 'SQLite rollback cleanup (phase 4): ';

export interface SupersededIdbCleanupDeps {
  indexedDb: IdbForDeletion;
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> & GenerationStorage;
  reporter: CleanupProblemReporter;
  /** Overridable so a test driving the never-answers case does not wait the full bound. */
  deletionTimeoutMs?: number;
}

/**
 * Drop any recorded name that is ALSO the name this device is opening right now.
 *
 * **This guard is what stops the cleanup deleting live data, and the route to it is real.**
 * The rolled-back marker survives a sign-out — it is on both localStorage survivor paths.
 * The per-database generation counters `cs_local_store_generation_<base>` survive NEITHER:
 * they are in no survivor list and match no preserved prefix. So walk it through:
 *
 *  1. A device rolls back from generation 0 to 1. The marker records `userdb-fr_abc`, and
 *     the live database becomes `userdb-fr_abc__g1`.
 *  2. The user signs out. The generation counters are wiped; the marker is kept.
 *  3. The same user signs in again. With no counter, the app reads generation 0 — so the
 *     LIVE database is `userdb-fr_abc`, the exact name the marker says to delete.
 *  4. Ten `Ready` boots later, with the operator's switch on, the cleanup would delete the
 *     database the user is using.
 *
 * So the check is structural rather than a matter of care at the call site, and it is
 * pinned by its own test. A refused name is dropped from the list as well as from the
 * deletion — keeping it would re-report on every boot for ever, and the database it names
 * is not superseded any more by definition.
 */
export function refuseLiveNames(
  recordedNames: readonly string[],
  storage: GenerationStorage,
  reporter: CleanupProblemReporter,
): string[] {
  const safe: string[] = [];
  for (const recordedName of recordedNames) {
    const liveName = currentLocalStoreName(storage, stripLocalStoreGeneration(recordedName));
    if (recordedName === liveName) {
      reporter.trackSilentProblem(SUPERSEDED_NAME_IS_LIVE_PROBLEM, {
        // the name itself, because the only way to understand one of these is to see which
        // generation the device ended up back on
        recorded_name: recordedName,
      });
      continue;
    }
    safe.push(recordedName);
  }
  return safe;
}

/**
 * Which of the device's actual databases belong to one superseded name.
 *
 * **Enumerate, then match — never derive.** The list handed to the deleter must be names
 * the device really has, not names computed from a rule. A derived list is a guess about
 * what exists, and this one authorises deletions.
 *
 * A match is the database itself, or one of its PouchDB view indexes. The view indexes are
 * included because they are usually the larger part of the duplicate this whole module
 * exists to remove, and they are derived data PouchDB rebuilds on demand.
 *
 * **The view-index prefix cannot reach a live generation, and it is worth seeing why.** A
 * live generation's index is `_pouch_userdb-fr_abc__g1-mrview-<hash>`. Matching the
 * superseded name `userdb-fr_abc` demands a `-` immediately after it, where the live name
 * has `_`. The same holds for `__g1` against `__g10`: the prefix demands `-` and the live
 * name has `0`. Two different base names cannot collide either — it would take one base
 * name to equal another followed by `-mrview-…`, and `-mrview-` never appears in a base
 * name, whose generation suffix is letters, digits and underscores only.
 */
export function matchesSupersededName(databaseName: string, supersededName: string): boolean {
  const exact = `${POUCH_DB_NAME_PREFIX}${supersededName}`;
  return databaseName === exact
    || databaseName.startsWith(`${exact}${VIEW_INDEX_SEPARATOR}`);
}

/** Why one attempt did not finish the job, and what to say about it. */
interface SupersededCleanupFailure {
  problem: string;
  context: { [key: string]: any };
  /** the databases still on the device, or '' when the enumeration itself failed */
  failedDbNames: string;
}

/** Read the attempt counter, treating anything unreadable as "no attempts yet". */
function readAttemptCount(storage: Pick<Storage, 'getItem'>): number {
  const stored = Number.parseInt(storage.getItem(SUPERSEDED_CLEANUP_ATTEMPTS_KEY) ?? '', 10);
  return Number.isFinite(stored) && stored > 0 ? stored : 0;
}

/** Run the deletions once, and say what stopped it finishing — or nothing, when it finished. */
async function attemptSupersededCleanupAsync(
  deps: SupersededIdbCleanupDeps,
  supersededNames: readonly string[],
  timeoutMs: number,
): Promise<SupersededCleanupFailure | undefined> {
  let results: IdbDeletionResult[];
  try {
    // `excludeViewIndexes: false`, because a superseded generation's view indexes are part
    // of what is being removed. The filtering to THIS generation happens below.
    const presentNames = await listPouchIndexedDbNamesAsync(deps.indexedDb, {
      excludeViewIndexes: false,
    });
    const toDelete = presentNames.filter(
      presentName => supersededNames.some(
        supersededName => matchesSupersededName(presentName, supersededName)));

    // A recorded name with nothing matching on the device counts as already gone — a
    // sign-out's own wipe may have taken it, or a re-migration's forward sweep. Nothing to
    // report: this is the ordinary ending, not a failure.
    if (toDelete.length === 0) return undefined;

    // no `shouldWaitFor`: this cleanup waits on every database it deletes. Nothing waits on
    // the cleanup itself, so the bound costs a boot nothing, and a database that stays
    // silent is a result this pass genuinely wants — it is what stops the list being cleared.
    results = await deleteIdbDatabasesAsync(deps.indexedDb, toDelete, {
      timeoutMs,
      logPrefix: LOG_PREFIX,
    });
  } catch (e) {
    // A FAILED ENUMERATION is a failed attempt, never an empty device. Believing an empty
    // answer here would clear the name list and orphan the duplicate permanently — the same
    // distinction the forward migration and the forward cleanup both rely on.
    return {
      problem: SUPERSEDED_CLEANUP_FAILED_PROBLEM,
      context: errorReportFields(e),
      failedDbNames: '',
    };
  }

  // Anything the browser did not confirm as gone is still on the device, so `Deleted` is the
  // only outcome that counts as done. BLOCKED counts as a survivor, because this pass can
  // come back on a later boot.
  const survivors = results.filter(result => result.outcome !== IdbDeletionOutcome.Deleted);
  if (survivors.length === 0) return undefined;

  const failedDbNames = survivors.map(survivor => survivor.name).join(',');
  return {
    problem: SUPERSEDED_DELETION_FAILED_PROBLEM,
    context: {
      failed_count: survivors.length,
      total_count: results.length,
      failed_db_names: failedDbNames,
      // say WHICH way each one went: refused, blocked and never-answered need different
      // follow-ups, and the names alone cannot tell them apart
      failed_outcomes: survivors.map(survivor => `${survivor.name}=${survivor.outcome}`).join(','),
      ...errorReportFields(survivors.find(survivor => survivor.error !== undefined)?.error),
    },
    failedDbNames,
  };
}

/**
 * Take the superseded names out of the rolled-back marker, leaving the rest of it alone.
 *
 * **The NAME LIST only, never the whole key.** The marker's other half is the re-arm
 * generation, which is what pins a rolled-back device to IndexedDB until an operator
 * raises the dial. Removing the key would un-pin the device and reopen the
 * migrate/roll-back/migrate lap the marker exists to close.
 *
 * Never throws: it runs on a path that must reach its end.
 */
function clearSupersededNames(deps: SupersededIdbCleanupDeps): void {
  try {
    const marker = readRolledBackMarker(deps.storage.getItem(SQLITE_MIGRATION_ROLLED_BACK_KEY));
    if (!marker) return;
    deps.storage.setItem(
      SQLITE_MIGRATION_ROLLED_BACK_KEY,
      serialiseRolledBackMarker({ ...marker, supersededIndexedDbNames: [] }),
    );
  } catch (e) {
    // The names surviving costs one more pass on a later boot, which is harmless — every
    // recorded name will then match nothing and the pass ends immediately. Reported anyway,
    // because a marker that can never be written is a device whose state is stuck.
    deps.reporter.trackSilentProblem(SUPERSEDED_CLEANUP_FAILED_PROBLEM, {
      stage: 'clearing the name list',
      ...errorReportFields(e),
    });
  }
}

/**
 * Delete the IndexedDB generation this device abandoned when it rolled back.
 *
 * The CALLER decides whether this device may run at all — that is
 * `mayDeleteSupersededIndexedDbCopy` in `sqlite-migration-decisions.ts`, which is where the
 * engine, the grace period and the operator's switch are checked. What is decided here is
 * only which databases the deletion may touch.
 *
 * Never throws: the call site fires it and forgets it, so a throw would surface as an
 * unhandled rejection in the middle of a boot.
 */
export async function cleanUpSupersededIndexedDbAsync(
  deps: SupersededIdbCleanupDeps,
): Promise<void> {
  const { storage, reporter } = deps;

  let supersededNames: string[];
  try {
    const marker = readRolledBackMarker(storage.getItem(SQLITE_MIGRATION_ROLLED_BACK_KEY));
    const recordedNames = marker?.supersededIndexedDbNames ?? [];
    if (recordedNames.length === 0) return;
    supersededNames = refuseLiveNames(recordedNames, storage, reporter);
  } catch (e) {
    // A browser with site data blocked throws from the `localStorage` GETTER itself, and
    // `refuseLiveNames` reads the generation counters through it too. This function is
    // documented never to throw and its callers fire it with `void`, so letting one escape
    // would be an unhandled rejection in the middle of a boot. Not deleting is the safe
    // direction — it costs disk space, never the user's last correct copy.
    reporter.trackSilentProblem(SUPERSEDED_CLEANUP_FAILED_PROBLEM, {
      stage: 'reading the recorded names',
      ...errorReportFields(e),
    });
    return;
  }

  if (supersededNames.length === 0) {
    // every recorded name turned out to be live. Nothing to delete, and nothing to keep
    // recording — see `refuseLiveNames` for why a refused name is dropped.
    clearSupersededNames(deps);
    return;
  }

  // count this boot BEFORE doing the work, so an attempt that dies half way through still
  // spends part of the budget rather than letting a wedging device try for ever.
  //
  // Wrapped for the same reason the read above is: a storage that refuses a WRITE — an
  // exhausted quota, Safari's private mode — throws from `setItem`, and this function is
  // documented never to throw while both its callers fire it with `void`. Giving up here
  // is the safe direction: this boot deletes nothing, and a device that can never record
  // an attempt keeps retrying on later boots rather than deleting something it should not
  // — which costs disk space, never the user's last correct copy.
  let attempts: number;
  try {
    attempts = readAttemptCount(storage) + 1;
    storage.setItem(SUPERSEDED_CLEANUP_ATTEMPTS_KEY, String(attempts));
  } catch (e) {
    reporter.trackSilentProblem(SUPERSEDED_CLEANUP_FAILED_PROBLEM, {
      stage: 'spending an attempt',
      ...errorReportFields(e),
    });
    return;
  }

  const failure = await attemptSupersededCleanupAsync(
    deps, supersededNames, deps.deletionTimeoutMs ?? CLEANUP_DELETION_TIMEOUT_MS);

  if (!failure) {
    clearSupersededNames(deps);
    try {
      storage.removeItem(SUPERSEDED_CLEANUP_ATTEMPTS_KEY);
    } catch {
      // nothing left to record: the deletion succeeded and the names are already cleared
    }
    console.log(`${LOG_PREFIX}deleted the superseded IndexedDB generation`);
    return;
  }

  reporter.trackSilentProblem(failure.problem, {
    ...failure.context,
    attempt: attempts,
    max_attempts: MAX_SUPERSEDED_CLEANUP_ATTEMPTS,
  });

  if (attempts >= MAX_SUPERSEDED_CLEANUP_ATTEMPTS) {
    // Stop trying, and clear the names so no later boot re-reports this for ever.
    //
    // **Dropping the names here is safe in a way it would NOT be for the SQLite files**,
    // and the difference is worth stating because the design doc warns against dropping
    // them. An orphaned IndexedDB database is still recoverable: if this device ever
    // migrates again, the forward cleanup's unfiltered `_pouch_*` sweep finds it. The
    // native plugin cannot list its own files, so a SQLite leftover has no such backstop.
    clearSupersededNames(deps);
    reporter.trackSilentProblem(SUPERSEDED_CLEANUP_GAVE_UP_PROBLEM, {
      attempts,
      failed_db_names: failure.failedDbNames,
      last_problem: failure.problem,
    });
  }
}
